import React from 'react';
import { PanelEntity } from './EntitySidePanel';
import { PanelRelationship } from './RelationshipPanel';

export interface ReadOnlyEntityInfo {
  kind: 'entity';
  entity: PanelEntity;
  openUrl: string;
}

export interface ReadOnlyRelationshipInfo {
  kind: 'relationship';
  rel: PanelRelationship;
  source: PanelEntity;
  target: PanelEntity;
  typeColor: string;
  openUrl: string;
}

export type ReadOnlyInfoPanelProps = {
  info: ReadOnlyEntityInfo | ReadOnlyRelationshipInfo;
  onClose: () => void;
};

const ENTITY_COLORS: Record<string, string> = {
  character: '#196773',
  location: '#5CA383',
  event: '#c97a1e',
};

const ENTITY_LABELS: Record<string, string> = {
  character: 'Character',
  location: 'Location',
  event: 'Event',
};

const strVal = (v: unknown): string | undefined =>
  typeof v === 'string' ? v : undefined;

const MetaRow: React.FC<{ label: string; value: string | undefined }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="ro-panel-meta-row">
      <span className="ro-panel-meta-label">{label}</span>
      <span className="ro-panel-meta-value">{value}</span>
    </div>
  );
};

const EntityView: React.FC<{ info: ReadOnlyEntityInfo }> = ({ info }) => {
  const { entity, openUrl } = info;
  const m = entity.metadata;
  const accentColor = ENTITY_COLORS[entity.entityType] ?? '#196773';

  const metaFields: { label: string; key: string }[] = entity.entityType === 'character'
    ? [
        { label: 'Archetype', key: 'archetype' },
        { label: 'Core traits', key: 'coreTraits' },
        { label: 'Fatal flaw', key: 'fatalFlaw' },
        { label: 'Motivation', key: 'motivation' },
      ]
    : entity.entityType === 'location'
    ? [
        { label: 'Climate', key: 'climate' },
        { label: 'Terrain', key: 'terrain' },
        { label: 'Government', key: 'government' },
      ]
    : [
        { label: 'When it occurs', key: 'whenItOccurs' },
        { label: 'Duration', key: 'duration' },
        { label: 'Cause', key: 'cause' },
      ];

  return (
    <>
      <div className="ro-panel-type-badge" style={{ borderColor: accentColor, color: accentColor }}>
        {ENTITY_LABELS[entity.entityType]}
      </div>
      {entity.description && (
        <p className="ro-panel-description">{entity.description}</p>
      )}
      <div className="ro-panel-meta">
        {metaFields.map(f => (
          <MetaRow key={f.key} label={f.label} value={strVal(m[f.key])} />
        ))}
      </div>
      <a
        href={openUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="ro-panel-open-link"
        style={{ borderColor: accentColor, color: accentColor }}
      >
        Edit in Chaptery Web →
      </a>
    </>
  );
};

const RelationshipView: React.FC<{ info: ReadOnlyRelationshipInfo }> = ({ info }) => {
  const { rel, source, target, typeColor, openUrl } = info;
  const m = rel.metadata;

  return (
    <>
      <div className="ro-panel-rel-type" style={{ borderLeftColor: typeColor }}>
        <span style={{ color: typeColor, textTransform: 'capitalize', fontWeight: 700 }}>
          {rel.relationshipType}
        </span>
      </div>
      <div className="ro-panel-rel-endpoints">
        <span className="ro-panel-rel-name">{source.name}</span>
        <span className="ro-panel-rel-arrow">→</span>
        <span className="ro-panel-rel-name">{target.name}</span>
      </div>
      {rel.description && (
        <p className="ro-panel-description">{rel.description}</p>
      )}
      <div className="ro-panel-meta">
        <MetaRow label="Origin" value={strVal(m.origin)} />
        <MetaRow label="Status" value={strVal(m.status)} />
        <MetaRow label="Story impact" value={strVal(m.storyImpact)} />
      </div>
      <a
        href={openUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="ro-panel-open-link"
        style={{ borderColor: typeColor, color: typeColor }}
      >
        Edit in Chaptery Web →
      </a>
    </>
  );
};

const ReadOnlyInfoPanel: React.FC<ReadOnlyInfoPanelProps> = ({ info, onClose }) => {
  const name = info.kind === 'entity' ? info.entity.name : `${info.source.name} & ${info.target.name}`;

  return (
    <div className="ro-panel">
      <div className="ro-panel-header">
        <span className="ro-panel-name">{name}</span>
        <button className="ro-panel-close" onClick={onClose} aria-label="Close">✕</button>
      </div>
      {info.kind === 'entity'
        ? <EntityView info={info} />
        : <RelationshipView info={info} />
      }
    </div>
  );
};

export default ReadOnlyInfoPanel;
